import React from 'react';
import {
    Button,
    DialogProps,
    Icon,
    PaginatedTable as Table,
    SortRule,
    Spinner,
    TableRow,
    Tooltip,
    WindowedTable,
} from '@economic/taco';
import { useCurrentAgreement } from '@economic/react-app';

import usePagePreferences from '../../../shared/hooks/usePagePreferences';
import { t } from '../../../shared/components/l10n/l10n';
import { ProductGroup } from '../../../../types/internal-api';
import { openWindow } from '../../../../utilities/openWindow';

type RowAction = (row: TableRow<ProductGroup>) => void;
interface Sorting {
    sortRules: SortRule<any>[] | undefined;
    onSort: (sortRules: SortRule<any>[] | undefined) => void;
}

interface ProductGroupsTableProps {
    data: ProductGroup[];
    showCountrySpecificFeatures: boolean;
    compact: boolean;
    totalItems: number;
    createDialog: (dialogProps: Partial<DialogProps>) => JSX.Element;
    onRowClick: (row: TableRow<ProductGroup>) => void;
    loading: boolean;
    rowActions: Record<string, RowAction>;
    sorting: Sorting;
}
interface AnchorWithTooltipProps {
    tooltip: string;
    onClick: (e: any) => void;
    children?: React.ReactNode;
}

const openAccountLedgerCard = (accountNumber?: number) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    accountNumber &&
        openWindow(`/generelt/dataedit.asp?form=8&ops=3804&Kladde=0&popup=yes&Kontonr=${encodeURIComponent(accountNumber)}`, {
            name: '_blank',
            width: 1080,
            height: 600,
        });
};

const SpanWithTooltip = ({ value }: any) => (
    <Tooltip title={value}>
        <span>{value}</span>
    </Tooltip>
);

const AnchorWithTooltip = ({ tooltip, onClick, children }: AnchorWithTooltipProps) =>
    children !== undefined ? (
        <Tooltip title={tooltip}>
            <a href="#" onClick={onClick}>
                {children}
            </a>
        </Tooltip>
    ) : null;

const ProductGroupsTable = ({
    rowActions,
    sorting,
    data,
    showCountrySpecificFeatures,
    loading,
    compact,
    onRowClick: handleRowClick,
    totalItems,
    createDialog,
}: ProductGroupsTableProps) => {
    const agreement = useCurrentAgreement();
    const [pagePrefs, setPagePrefs] = usePagePreferences();

    const hasProjectModule = !!agreement.modules.project;
    const hasInventoryModule = !!agreement.modules.inventory;

    return React.useMemo(
        () =>
            compact ? (
                <>
                    <WindowedTable<ProductGroup> {...sorting} data={data} onRowClick={handleRowClick} autoFocus>
                        <Table.Column
                            sort="asc"
                            flex="0 0 100px"
                            accessor="id"
                            align="left"
                            headRenderer={t`FORM_FIELD_LABEL_NUMBER`}
                        />
                        <Table.Column accessor="name" align="left" headRenderer={t`FORM_FIELD_LABEL_NAME`} />
                    </WindowedTable>
                    <p className="text-right font-bold mt-4 mb-0">
                        {t('POPUPSEARCH_XRECORDSFOUND', {
                            SEARCHCOUNT: data.length.toString(),
                            TOTALCOUNT: totalItems.toString(),
                        })}
                    </p>
                </>
            ) : (
                <Table<ProductGroup>
                    {...sorting}
                    {...rowActions}
                    onRowClick={handleRowClick}
                    pageSize={pagePrefs.pageSize}
                    onPaginate={(_, nextPageSize) => {
                        setPagePrefs({ pageSize: nextPageSize });
                    }}
                    // Product groups doesn't have server side pagination and we always load all the product groups
                    // data, so when swr is validating the cache, for that time by passing empty array, we can show the
                    // loading state.
                    data={loading ? [] : data}
                    emptyStateRenderer={() => {
                        if (!loading && totalItems === 0) {
                            return (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <p className="text-2xl font-hairline">{t`FORM_GOODSGROUPS_EMPTY_STATE`}</p>
                                    <Button appearance="primary" dialog={createDialog}>
                                        <Icon name="document-create-entry" />
                                        {t`GOODSGROUPS_CREATETEXT`}
                                    </Button>
                                </div>
                            );
                        }
                        if (loading) {
                            return (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <Spinner delay={0} className="mx-auto m-2" />
                                </div>
                            );
                        }

                        return null;
                    }}>
                    <Table.Column
                        flex="1 0 7ch"
                        sort="asc"
                        accessor="id"
                        align="left"
                        headRenderer={<SpanWithTooltip value={t`FORM_FIELD_LABEL_NUMBER`} />}
                        cellRenderer={SpanWithTooltip}
                    />
                    <Table.Column
                        flex="2 1 14ch"
                        accessor="name"
                        align="left"
                        headRenderer={<SpanWithTooltip value={t`FORM_FIELD_LABEL_NAME`} />}
                        cellRenderer={SpanWithTooltip}
                    />
                    <Table.Column<ProductGroup>
                        flex="1 0 7ch"
                        accessor="accountingRules.domesticAccountNumber"
                        align="left"
                        headRenderer={<SpanWithTooltip value={t`SHARED_RECEIVABLESVATZONE_DENMARK`} />}
                        cellRenderer={({ value, row }) => (
                            <AnchorWithTooltip
                                tooltip={row.values.accountingRules.domesticAccount}
                                onClick={openAccountLedgerCard(row.values.accountingRules.domesticAccountNumber)}>
                                {value}
                            </AnchorWithTooltip>
                        )}
                    />
                    <Table.Column<ProductGroup>
                        flex="1 0 7ch"
                        accessor="accountingRules.euAccountNumber"
                        align="left"
                        headRenderer={<SpanWithTooltip value={t`SHARED_RECEIVABLESVATZONE_EU`} />}
                        cellRenderer={({ value, row }) => (
                            <AnchorWithTooltip
                                tooltip={row.values.accountingRules.euAccount}
                                onClick={openAccountLedgerCard(row.values.accountingRules.euAccountNumber)}>
                                {value}
                            </AnchorWithTooltip>
                        )}
                    />
                    <Table.Column<ProductGroup>
                        flex="1 0 7ch"
                        accessor="accountingRules.abroadAccountNumber"
                        align="left"
                        headRenderer={<SpanWithTooltip value={t`SHARED_RECEIVABLESVATZONE_OUTSIDEEU`} />}
                        cellRenderer={({ value, row }) => (
                            <AnchorWithTooltip
                                tooltip={row.values.accountingRules.abroadAccount}
                                onClick={openAccountLedgerCard(row.values.accountingRules.abroadAccountNumber)}>
                                {value}
                            </AnchorWithTooltip>
                        )}
                    />
                    <Table.Column<ProductGroup>
                        flex="1 0 7ch"
                        accessor="accountingRules.domesticWithoutVatAccountNumber"
                        align="left"
                        headRenderer={<SpanWithTooltip value={t`SHARED_RECEIVABLESVATZONE_DOMESTIC_NoVAT`} />}
                        cellRenderer={({ value, row }) => (
                            <AnchorWithTooltip
                                tooltip={row.values.accountingRules.domesticWithoutVatAccount}
                                onClick={openAccountLedgerCard(row.values.accountingRules.domesticWithoutVatAccountNumber)}>
                                {value}
                            </AnchorWithTooltip>
                        )}
                    />
                    {hasProjectModule && (
                        <Table.Column<ProductGroup>
                            flex="1 0 7ch"
                            accessor="accountingRules.currentProjectsAccountNumber"
                            align="left"
                            headRenderer={<SpanWithTooltip value={t`SHARED_RECEIVABLESVATZONE_PROJECTACCOUNT`} />}
                            cellRenderer={({ value, row }) => (
                                <AnchorWithTooltip
                                    tooltip={row.values.accountingRules.currentProjectsAccount}
                                    onClick={openAccountLedgerCard(row.values.accountingRules.currentProjectsAccountNumber)}>
                                    {value}
                                </AnchorWithTooltip>
                            )}
                        />
                    )}
                    {hasInventoryModule && (
                        <Table.Column
                            flex="1 0 7ch"
                            accessor="isServiceProducts"
                            align="left"
                            headRenderer={<SpanWithTooltip value={t`FORM_GOODSGROUPS_COLHEADER_STOCKACCOUNT`} />}
                            cellRenderer={({ value }) => (
                                <SpanWithTooltip value={value ? t`SHARED_YESORNO_NO` : t`SHARED_YESORNO_YES`} />
                            )}
                        />
                    )}
                    {showCountrySpecificFeatures && (
                        <Table.Column
                            flex="1 0 7ch"
                            accessor="hasCountrySpecificVat"
                            align="left"
                            headRenderer={<SpanWithTooltip value={t`SHARED_RECEIVABLESVATZONE_HAS_COUNTRY_SPECIFIC_VAT`} />}
                            cellRenderer={({ value }) => (
                                <SpanWithTooltip value={value ? t`SHARED_YESORNO_YES` : t`SHARED_YESORNO_NO`} />
                            )}
                        />
                    )}
                </Table>
            ),
        // We don't need sorting as dependency variable because we don't have server side sorting for product groups.
        [data, loading, totalItems, compact]
    );
};

export default ProductGroupsTable;
